import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ArrowLeft, MessageCircle, Save, Upload, FileText, Image as ImageIcon, Trash2, Receipt, Loader2, AlertTriangle, Droplet, Trash } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useClinic } from "@/store/ClinicContext";
import { PatientFields, EMPTY_PATIENT } from "@/components/pacientes/PatientFields";
import { brl, getOverview, uploadFile, openFile, deleteFile, downloadReceipt, markNoShow, waLink } from "@/components/pacientes/patientApi";

const ST = {
  realizada: ["Realizada", "bg-emerald-50 text-emerald-700"], faltou: ["Faltou", "bg-amber-50 text-amber-700"],
  cancelada: ["Cancelada", "bg-rose-50 text-rose-600"], futura: ["Agendada", "bg-blue-50 text-blue-700"],
  pendente: ["Sem registro", "bg-slate-100 text-slate-500"],
};
const Pill = ({ s, id }) => <span data-testid={`hist-status-${id}`} className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${ST[s]?.[1]}`}>{ST[s]?.[0] || s}</span>;
const Empty = ({ t }) => <p className="py-10 text-center text-sm text-slate-400">{t}</p>;

const DadosTab = ({ patient, onSaved }) => {
  const { updatePatient, removePatient, healthPlans } = useClinic();
  const [p, setP] = useState({ ...EMPTY_PATIENT, ...patient });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!p.name.trim()) return toast.error("Informe o nome.");
    setSaving(true);
    const ok = await updatePatient(patient.id, p).finally(() => setSaving(false));
    if (ok) { toast.success("Cadastro atualizado"); onSaved(); } else toast.error("Falha ao salvar");
  };
  const del = async () => {
    if (!window.confirm(`Excluir o cadastro de ${patient.name}? O histórico de consultas é mantido.`)) return;
    await removePatient(patient.id);
    toast.success("Paciente excluído");
    onSaved(true);
  };
  return (
    <div>
      <PatientFields value={p} onChange={setP} plans={healthPlans} />
      <div className="mt-5 flex flex-wrap justify-between gap-2 border-t border-slate-100 pt-4">
        <button data-testid="patient-delete-btn" onClick={del} className="flex items-center gap-1.5 rounded-xl border border-rose-200 px-4 py-2.5 text-sm font-semibold text-rose-600 hover:bg-rose-50"><Trash className="h-4 w-4" />Excluir</button>
        <button data-testid="patient-save-btn" onClick={save} disabled={saving} className="flex items-center gap-1.5 rounded-xl bg-blue-600 px-5 py-2.5 text-sm font-bold text-white hover:bg-blue-700 disabled:opacity-60"><Save className="h-4 w-4" />{saving ? "Salvando…" : "Salvar alterações"}</button>
      </div>
    </div>
  );
};

const ConsultasTab = ({ items, reload }) => {
  const noShow = async (a) => { try { await markNoShow(a.id); toast.success("Falta registrada"); reload(); } catch (e) { toast.error(e.message); } };
  if (!items.length) return <Empty t="Nenhuma consulta encontrada para este paciente." />;
  return (
    <div className="divide-y divide-slate-100">
      {items.map((a) => (
        <div key={a.id} data-testid={`hist-row-${a.id}`} className="flex flex-wrap items-center gap-x-4 gap-y-1 py-3">
          <div className="w-28 shrink-0"><p className="font-mono-num text-sm font-bold text-slate-900">{a.date}</p><p className="text-xs text-slate-500">{a.time}</p></div>
          <div className="min-w-0 flex-1 basis-40"><p className="truncate text-sm font-semibold text-slate-800">{a.procedure || "Consulta"} · {a.modality === "online" ? "Telemedicina" : "Presencial"}</p><p className="truncate text-xs text-slate-500">{a.doctor} · {a.kind}</p></div>
          <Pill s={a.status} id={a.id} />
          {a.status === "pendente" && <button data-testid={`hist-noshow-${a.id}`} onClick={() => noShow(a)} className="rounded-lg border border-amber-200 px-2 py-1 text-[11px] font-semibold text-amber-700 hover:bg-amber-50">Marcar falta</button>}
        </div>
      ))}
    </div>
  );
};

const FilesBox = ({ patientId, files, reload }) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const [cat, setCat] = useState("Exame");
  const onPick = async (e) => {
    const list = [...(e.target.files || [])];
    e.target.value = "";
    if (!list.length) return;
    setBusy(true);
    try { for (const f of list) await uploadFile(patientId, f, cat); toast.success(`${list.length} arquivo(s) anexado(s)`); reload(); }
    catch (err) { toast.error(err.message); } finally { setBusy(false); }
  };
  const rm = async (f) => { if (!window.confirm(`Remover ${f.name}?`)) return; try { await deleteFile(f); reload(); } catch (e) { toast.error(e.message); } };
  return (
    <div className="mt-6 rounded-xl border border-slate-200 p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm font-bold text-slate-800">Anexos de exames</p>
        <div className="flex items-center gap-2">
          <select data-testid="file-category-select" value={cat} onChange={(e) => setCat(e.target.value)} className="rounded-lg border border-slate-200 px-2 py-1.5 text-xs">
            {["Exame", "Laudo", "Imagem", "Receita anterior", "Outro"].map((c) => <option key={c}>{c}</option>)}
          </select>
          <input ref={ref} data-testid="file-upload-input" type="file" multiple accept="application/pdf,image/*" className="hidden" onChange={onPick} />
          <button data-testid="file-upload-btn" onClick={() => ref.current?.click()} disabled={busy} className="flex items-center gap-1.5 rounded-lg bg-slate-900 px-3 py-1.5 text-xs font-bold text-white hover:bg-blue-700 disabled:opacity-60">{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}Anexar PDF/imagem</button>
        </div>
      </div>
      {files.length === 0 ? <p className="mt-3 text-xs text-slate-400">Nenhum anexo. PDF ou imagem até 15 MB.</p> : (
        <div className="mt-3 grid gap-2 sm:grid-cols-2">
          {files.map((f) => (
            <div key={f.id} data-testid={`file-${f.id}`} className="flex items-center gap-2 rounded-lg bg-slate-50 px-3 py-2">
              {f.content_type === "application/pdf" ? <FileText className="h-4 w-4 shrink-0 text-rose-500" /> : <ImageIcon className="h-4 w-4 shrink-0 text-blue-500" />}
              <button data-testid={`file-open-${f.id}`} onClick={() => openFile(f).catch((e) => toast.error(e.message))} className="min-w-0 flex-1 truncate text-left text-xs font-semibold text-slate-700 hover:text-blue-700">{f.name}</button>
              <span className="shrink-0 text-[10px] text-slate-400">{f.category} · {new Date(f.created_at).toLocaleDateString("pt-BR")}</span>
              <button data-testid={`file-delete-${f.id}`} onClick={() => rm(f)} className="shrink-0 text-slate-400 hover:text-rose-500"><Trash2 className="h-3.5 w-3.5" /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const ProntuarioTab = ({ patientId, files, reload }) => {
  const { listRecords, addRecord, downloadRecordPdf } = useClinic();
  const [records, setRecords] = useState([]);
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);
  const load = useCallback(async () => { setRecords(await listRecords(patientId)); }, [listRecords, patientId]);
  useEffect(() => { load(); }, [load]);
  const save = async () => {
    if (!text.trim()) return toast.error("Escreva a evolução clínica.");
    setSaving(true);
    try { await addRecord(patientId, text.trim()); setText(""); toast.success("Evolução registrada"); load(); }
    catch (e) { toast.error(e.message || "Falha ao salvar"); } finally { setSaving(false); }
  };
  return (
    <div>
      <textarea data-testid="evolucao-textarea" value={text} onChange={(e) => setText(e.target.value)} rows={5} placeholder="Evolução clínica do atendimento, conduta, prescrição…" className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-blue-200" />
      <div className="mt-2 flex justify-end"><button data-testid="evolucao-save-btn" onClick={save} disabled={saving} className="flex items-center gap-1.5 rounded-xl bg-blue-600 px-4 py-2 text-sm font-bold text-white hover:bg-blue-700 disabled:opacity-60"><Save className="h-4 w-4" />{saving ? "Salvando…" : "Registrar evolução"}</button></div>
      <div className="mt-4 space-y-3">
        {records.length === 0 ? <Empty t="Nenhuma evolução registrada." /> : records.map((r) => (
          <div key={r.id} data-testid={`evolucao-${r.id}`} className="rounded-xl border border-slate-100 bg-slate-50/60 p-4">
            <div className="mb-2 flex flex-wrap items-center justify-between gap-2 text-xs text-slate-500">
              <span className="font-semibold text-slate-700">{new Date(r.created_at).toLocaleString("pt-BR")} · {r.author_name || r.author || "Médico"}</span>
              <button data-testid={`evolucao-pdf-${r.id}`} onClick={() => downloadRecordPdf(r.id)} className="font-semibold text-blue-600 hover:underline">PDF</button>
            </div>
            <p className="whitespace-pre-wrap text-sm text-slate-700">{r.text}</p>
          </div>
        ))}
      </div>
      <FilesBox patientId={patientId} files={files} reload={reload} />
    </div>
  );
};

const FinanceiroTab = ({ items, totals }) => {
  const rec = (id) => downloadReceipt(id).catch((e) => toast.error(e.message));
  return (
    <div>
      <div className="mb-4 grid grid-cols-2 gap-3">
        <div className="rounded-xl bg-emerald-50 p-3"><p className="text-[11px] font-semibold uppercase text-emerald-700">Total pago</p><p data-testid="fin-total-paid" className="font-mono-num text-lg font-bold text-emerald-800">{brl(totals.paid)}</p></div>
        <div className="rounded-xl bg-amber-50 p-3"><p className="text-[11px] font-semibold uppercase text-amber-700">Em aberto</p><p data-testid="fin-total-open" className="font-mono-num text-lg font-bold text-amber-800">{brl(totals.open)}</p></div>
      </div>
      {items.length === 0 ? <Empty t="Nenhuma transação registrada." /> : (
        <div className="divide-y divide-slate-100">
          {items.map((f) => (
            <div key={f.id} data-testid={`fin-row-${f.id}`} className="flex flex-wrap items-center gap-x-4 gap-y-1 py-3">
              <div className="w-28 shrink-0"><p className="font-mono-num text-sm font-bold text-slate-900">{f.date}</p><p className="text-xs text-slate-500">{f.time}</p></div>
              <div className="min-w-0 flex-1 basis-40"><p className="truncate text-sm font-semibold text-slate-800">{f.description}</p><p className="truncate text-xs text-slate-500">{f.method}{f.net_value != null ? ` · líquido ${brl(f.net_value)} (taxa ${brl(f.op_fee)})` : ""}</p></div>
              <span className="font-mono-num text-sm font-bold text-slate-800">{brl(f.value)}</span>
              <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${f.paid ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>{f.status}</span>
              {f.receipt && <button data-testid={`fin-receipt-${f.id}`} onClick={() => rec(f.id)} className="flex items-center gap-1 rounded-lg border border-slate-200 px-2 py-1 text-[11px] font-semibold text-slate-600 hover:bg-slate-50"><Receipt className="h-3.5 w-3.5" />Recibo</button>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const Header = ({ p, onBack }) => {
  const wa = waLink(p.phone, p.name);
  return (
    <div className="flex flex-wrap items-start justify-between gap-3 border-b border-slate-100 px-4 py-4 sm:px-6">
      <div className="flex min-w-0 items-center gap-3">
        <button data-testid="patient-back-btn" onClick={onBack} className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-100"><ArrowLeft className="h-5 w-5" /></button>
        <div className="min-w-0">
          <h3 data-testid="patient-profile-name" className="font-heading truncate text-lg font-bold text-slate-900">{p.name}</h3>
          <p className="truncate text-xs text-slate-500">{[p.cpf, p.phone, p.convenio, p.carteirinha && `Cart. ${p.carteirinha}`].filter(Boolean).join(" · ")}</p>
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {p.alergias && <span data-testid="patient-allergy-badge" className="flex items-center gap-1 rounded-full bg-rose-50 px-2 py-0.5 text-[11px] font-semibold text-rose-700"><AlertTriangle className="h-3 w-3" />Alergia: {p.alergias}</span>}
            {p.tipo_sanguineo && <span className="flex items-center gap-1 rounded-full bg-red-50 px-2 py-0.5 text-[11px] font-semibold text-red-700"><Droplet className="h-3 w-3" />{p.tipo_sanguineo}</span>}
            <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${p.active ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{p.active ? "Ativo" : "Inativo"}</span>
          </div>
        </div>
      </div>
      {wa ? <a data-testid="patient-whatsapp-btn" href={wa} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 rounded-xl bg-emerald-600 px-4 py-2.5 text-sm font-bold text-white hover:bg-emerald-700"><MessageCircle className="h-4 w-4" />WhatsApp</a>
        : <span className="text-xs text-slate-400">Sem telefone cadastrado</span>}
    </div>
  );
};

export const PatientProfile = ({ id, onBack }) => {
  const [data, setData] = useState(null);
  const load = useCallback(async () => {
    try { setData(await getOverview(id)); } catch (e) { toast.error(e.message); onBack(); }
  }, [id, onBack]);
  useEffect(() => { load(); }, [load]);
  if (!data) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-blue-500" /></div>;
  const tab = "rounded-lg text-xs font-semibold data-[state=active]:bg-white data-[state=active]:text-blue-700 data-[state=active]:shadow-sm sm:text-sm";
  return (
    <div data-testid="patient-profile" className="rounded-2xl border border-slate-200/70 bg-white shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
      <Header p={data.patient} onBack={onBack} />
      <Tabs defaultValue="dados" className="px-4 py-4 sm:px-6">
        <TabsList className="mb-4 grid h-auto w-full grid-cols-2 gap-1 rounded-xl bg-slate-100 p-1 sm:grid-cols-4">
          <TabsTrigger value="dados" data-testid="ptab-dados" className={tab}>Dados</TabsTrigger>
          <TabsTrigger value="consultas" data-testid="ptab-consultas" className={tab}>Consultas ({data.appointments.length})</TabsTrigger>
          <TabsTrigger value="prontuario" data-testid="ptab-prontuario" className={tab}>Prontuário</TabsTrigger>
          <TabsTrigger value="financeiro" data-testid="ptab-financeiro" className={tab}>Financeiro</TabsTrigger>
        </TabsList>
        <TabsContent value="dados"><DadosTab key={data.patient.id} patient={data.patient} onSaved={(deleted) => (deleted === true ? onBack() : load())} /></TabsContent>
        <TabsContent value="consultas"><ConsultasTab items={data.appointments} reload={load} /></TabsContent>
        <TabsContent value="prontuario"><ProntuarioTab patientId={data.patient.id} files={data.files} reload={load} /></TabsContent>
        <TabsContent value="financeiro"><FinanceiroTab items={data.financial} totals={data.totals} /></TabsContent>
      </Tabs>
    </div>
  );
};
